import {Values} from '@wix/devzai-utils-common';

/**
 * Status of a suspense async value.
 */
export const SuspenseAsyncValueStatus = {
    Idle: 'idle',
    Pending: 'pending',
    Resolved: 'resolved',
    Rejected: 'rejected',
} as const;

export type SuspenseAsyncValueStatus = Values<typeof SuspenseAsyncValueStatus>;

/**
 * A Suspense-compatible async value holder.
 * 
 * Unlike AsyncCachedValue (which deletes cache entries on rejection for retry semantics),
 * this utility maintains error state to allow ErrorBoundary to catch errors consistently.
 */
export interface ISuspenseAsyncValue<T> {
    /**
     * Read value for Suspense. Behavior depends on state:
     * - Resolved: returns the cached value
     * - Pending: throws the promise (Suspense catches it)
     * - Rejected: throws the error (ErrorBoundary catches it)
     * - Idle: starts fetch, throws the promise
     */
    read(): T;

    /**
     * Invalidate cache, resetting to idle state.
     * Next read() will start a fresh fetch.
     */
    invalidate(): void;

    /**
     * Prefetch value without reading (warms cache).
     * No-op if already pending, resolved, or rejected.
     */
    prefetch(): void;

    /**
     * Check if a fetch is in progress.
     */
    isPending(): boolean;

    /**
     * Get current status.
     */
    getStatus(): SuspenseAsyncValueStatus;

    /**
     * Get cached value without triggering fetch.
     * Returns undefined if not resolved.
     */
    getCachedValue(): T | undefined;
}

/**
 * Creates a Suspense-compatible async value holder.
 * 
 * This utility is designed specifically for React Suspense integration.
 * Key difference from AsyncCachedValue: rejected promises are kept (not deleted),
 * allowing ErrorBoundary to catch errors consistently.
 * 
 * @param fetchFunction - Async function that fetches the value
 * @returns A suspense async value interface
 */
export function suspenseAsyncValueCreate<T>(
    fetchFunction: () => Promise<T>
): ISuspenseAsyncValue<T> {
    let status: SuspenseAsyncValueStatus = SuspenseAsyncValueStatus.Idle;
    let promise: Promise<T> | undefined;
    let resolvedValue: T | undefined;
    let rejectedError: Error | undefined;

    function startFetch(): Promise<T> {
        status = SuspenseAsyncValueStatus.Pending;
        promise = fetchFunction().then(
            (value) => {
                status = SuspenseAsyncValueStatus.Resolved;
                resolvedValue = value;
                return value;
            },
            (error) => {
                status = SuspenseAsyncValueStatus.Rejected;
                rejectedError = error instanceof Error ? error : new Error(String(error));
                throw rejectedError;
            }
        );
        return promise;
    }

    return {
        read(): T {
            switch (status) {
                case SuspenseAsyncValueStatus.Resolved:
                    return resolvedValue as T;
                case SuspenseAsyncValueStatus.Pending:
                    throw promise;
                case SuspenseAsyncValueStatus.Rejected:
                    throw rejectedError;
                case SuspenseAsyncValueStatus.Idle:
                    throw startFetch();
            }
        },

        invalidate(): void {
            status = SuspenseAsyncValueStatus.Idle;
            promise = undefined;
            resolvedValue = undefined;
            rejectedError = undefined;
        },

        prefetch(): void {
            if (status === SuspenseAsyncValueStatus.Idle) {
                startFetch();
            }
        },

        isPending(): boolean {
            return status === SuspenseAsyncValueStatus.Pending;
        },

        getStatus(): SuspenseAsyncValueStatus {
            return status;
        },

        getCachedValue(): T | undefined {
            return status === SuspenseAsyncValueStatus.Resolved ? resolvedValue : undefined;
        }
    };
}

/**
 * Creates a Suspense-compatible async value holder with params support.
 * Each unique params combination gets its own cached value.
 * 
 * @param hashFunction - Function to create cache key from params
 * @param fetchFunction - Async function that fetches the value given params
 * @returns A suspense async value with params interface
 */
export function suspenseAsyncValueCreateWithParams<T, P>(
    hashFunction: (params: P) => string,
    fetchFunction: (params: P) => Promise<T>
): ISuspenseAsyncValueWithParams<T, P> {
    const cache = new Map<string, ISuspenseAsyncValue<T>>();

    function getOrCreateEntry(params: P): ISuspenseAsyncValue<T> {
        const key = hashFunction(params);
        let entry = cache.get(key);
        if (!entry) {
            entry = suspenseAsyncValueCreate(() => fetchFunction(params));
            cache.set(key, entry);
        }
        return entry;
    }

    return {
        read(params: P): T {
            return getOrCreateEntry(params).read();
        },

        invalidate(params: P): void {
            const key = hashFunction(params);
            const entry = cache.get(key);
            if (entry) {
                entry.invalidate();
                cache.delete(key);
            }
        },

        invalidateAll(): void {
            cache.forEach(entry => entry.invalidate());
            cache.clear();
        },

        prefetch(params: P): void {
            getOrCreateEntry(params).prefetch();
        },

        isPending(params: P): boolean {
            const key = hashFunction(params);
            const entry = cache.get(key);
            return entry?.isPending() ?? false;
        },

        getStatus(params: P): SuspenseAsyncValueStatus {
            const key = hashFunction(params);
            const entry = cache.get(key);
            return entry?.getStatus() ?? SuspenseAsyncValueStatus.Idle;
        },

        getCachedValue(params: P): T | undefined {
            const key = hashFunction(params);
            const entry = cache.get(key);
            return entry?.getCachedValue();
        }
    };
}

/**
 * A Suspense-compatible async value holder with params support.
 */
export interface ISuspenseAsyncValueWithParams<T, P> {
    /**
     * Read value for given params. Behavior depends on state:
     * - Resolved: returns the cached value
     * - Pending: throws the promise (Suspense catches it)
     * - Rejected: throws the error (ErrorBoundary catches it)
     * - Idle: starts fetch, throws the promise
     */
    read(params: P): T;

    /**
     * Invalidate cache for specific params.
     */
    invalidate(params: P): void;

    /**
     * Invalidate all cached entries.
     */
    invalidateAll(): void;

    /**
     * Prefetch value for given params without reading.
     */
    prefetch(params: P): void;

    /**
     * Check if a fetch is in progress for given params.
     */
    isPending(params: P): boolean;

    /**
     * Get current status for given params.
     */
    getStatus(params: P): SuspenseAsyncValueStatus;

    /**
     * Get cached value for given params without triggering fetch.
     */
    getCachedValue(params: P): T | undefined;
}
